import { useEffect } from 'react';

export interface OrganizationSchema {
  '@context': 'https://schema.org';
  '@type': 'Organization';
  name: string;
  url: string;
  logo?: string;
  description?: string;
  contactPoint?: {
    '@type': 'ContactPoint';
    contactType: string;
    email?: string;
    telephone?: string;
  };
  sameAs?: string[]; // Social media profiles
}

export interface SoftwareApplicationSchema {
  '@context': 'https://schema.org';
  '@type': 'SoftwareApplication';
  name: string;
  applicationCategory: string;
  operatingSystem: string;
  description: string;
  url: string;
  offers?: {
    '@type': 'Offer';
    price: string;
    priceCurrency: string;
    availability: string;
  }[];
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: string;
    ratingCount: string;
  };
  screenshot?: string;
}

export interface WebSiteSchema {
  '@context': 'https://schema.org';
  '@type': 'WebSite';
  name: string;
  url: string;
  potentialAction?: {
    '@type': 'SearchAction';
    target: {
      '@type': 'EntryPoint';
      urlTemplate: string;
    };
    'query-input': string;
  };
}

export interface FAQPageSchema {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: {
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }[];
}

export interface BreadcrumbListSchema {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: {
    '@type': 'ListItem';
    position: number;
    name: string;
    item: string;
  }[];
}

export interface ItemListSchema {
  '@context': 'https://schema.org';
  '@type': 'ItemList';
  name: string;
  description?: string;
  itemListElement: {
    '@type': 'ListItem';
    position: number;
    name: string;
    description?: string;
  }[];
}

type SchemaType = 
  | OrganizationSchema 
  | SoftwareApplicationSchema 
  | WebSiteSchema 
  | FAQPageSchema 
  | BreadcrumbListSchema
  | ItemListSchema;

interface StructuredDataProps {
  schemas: SchemaType[];
}

/**
 * StructuredData component that injects JSON-LD structured data into the page
 */
export const StructuredData = ({ schemas }: StructuredDataProps) => {
  useEffect(() => {
    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());
    
    // Add new structured data scripts
    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `structured-data-${index}`;
      script.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(script);
    });
    
    // Cleanup function
    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.id.startsWith('structured-data-')) {
          script.remove();
        }
      });
    };
  }, [schemas]);
  
  return null; // This component doesn't render anything
};

/**
 * Helper function to create Organization schema
 */
export const createOrganizationSchema = (
  name: string,
  url: string,
  options?: {
    logo?: string;
    description?: string;
    email?: string;
    telephone?: string;
    socialProfiles?: string[];
  }
): OrganizationSchema => {
  const schema: OrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
  };
  
  if (options?.logo) {
    schema.logo = options.logo;
  }
  
  if (options?.description) {
    schema.description = options.description;
  }
  
  if (options?.email || options?.telephone) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
    };
    if (options.email) {
      schema.contactPoint.email = options.email;
    }
    if (options.telephone) {
      schema.contactPoint.telephone = options.telephone;
    }
  }
  
  if (options?.socialProfiles && options.socialProfiles.length > 0) {
    schema.sameAs = options.socialProfiles;
  }
  
  return schema;
};

/**
 * Helper function to create SoftwareApplication schema
 */
export const createSoftwareApplicationSchema = (
  name: string,
  description: string,
  url: string,
  options?: {
    applicationCategory?: string;
    operatingSystem?: string;
    offers?: Array<{
      price: string;
      priceCurrency: string;
      availability?: string;
    }>;
    screenshot?: string;
    aggregateRating?: {
      '@type': 'AggregateRating';
      ratingValue: string;
      ratingCount: string;
    };
  }
): SoftwareApplicationSchema => {
  const schema: SoftwareApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    description,
    url,
    applicationCategory: options?.applicationCategory || 'BusinessApplication',
    operatingSystem: options?.operatingSystem || 'Web Browser',
  };
  
  if (options?.offers && options.offers.length > 0) {
    schema.offers = options.offers.map(offer => ({
      '@type': 'Offer' as const,
      price: offer.price,
      priceCurrency: offer.priceCurrency,
      availability: offer.availability || 'https://schema.org/InStock',
    }));
  }
  
  if (options?.screenshot) {
    schema.screenshot = options.screenshot;
  }
  
  if (options?.aggregateRating) {
    schema.aggregateRating = options.aggregateRating;
  }
  
  return schema;
};

/**
 * Helper function to create WebSite schema
 */
export const createWebSiteSchema = (
  name: string,
  url: string,
  searchUrl?: string
): WebSiteSchema => {
  const schema: WebSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
  };
  
  if (searchUrl) {
    schema.potentialAction = {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: searchUrl,
      },
      'query-input': 'required name=search_term_string',
    };
  }
  
  return schema;
};

/**
 * Helper function to create FAQPage schema
 */
export const createFAQPageSchema = (
  faqs: Array<{ question: string; answer: string }>
): FAQPageSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

/**
 * Helper function to create BreadcrumbList schema
 */
export const createBreadcrumbListSchema = (
  items: Array<{ name: string; url: string }>
): BreadcrumbListSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

/**
 * Helper function to create ItemList schema (useful for features, benefits, etc.)
 */
export const createItemListSchema = (
  name: string,
  items: Array<{ name: string; description?: string }>,
  description?: string
): ItemListSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    ...(description && { description }),
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.description && { description: item.description }),
    })),
  };
};

